<template>
    <div class="device-groups-editor-component">
        <Loader v-if="loading" />
        <b-alert variant="danger" :show="loadError">
            {{$t('group_load_error')}}
            <b-button size="sm" :disabled="loading || busy" @click="loadDeviceGroupsData">{{$t('retry')}}</b-button>
        </b-alert>
        <b-alert variant="danger" :show="writeError">{{$t('group_write_error')}}</b-alert>
        <div class="toolbar" v-if="!question">
            <b-button
                v-if="!editedItem"
                variant="primary"
                :disabled="loading || busy || loadError"
                @click="openCreateForm"
            >
                {{$t('device_groups.create')}}
            </b-button>
            <b-button
                v-if="editedItem"
                variant="primary"
                :disabled="busy"
                @click="submitItem"
            >
                {{$t('device_groups.submit')}}
            </b-button>
            <b-button
                v-if="editedItem"
                :disabled="busy"
                @click="cancelEdit"
            >
                {{$t('device_groups.cancel')}}
            </b-button>
        </div>
        <span v-if="!loading && !loadError && !items.length && !editedItem && !question">{{$t('empty_list')}}</span>
        <b-list-group class="device-groups-list" v-if="!loading && items.length && !editedItem && !question" dark>
            <b-list-group-item
                class="device-groups-list-item"
                v-for="item in items"
                v-bind:key="item.id"
                @click="selectGroup(item)"
                v-bind:class="{active: item.active}"
                dark
            >
                <div class="list-item-container">
                    <span>{{item.name}}</span>
                    <div class="list-item-toolbar">
                        <b-icon-pencil @click.stop="editItem(item)" />
                        <b-icon-trash @click.stop="deleteItem(item)" />
                    </div>
                </div>
            </b-list-group-item>
        </b-list-group>
        <b-form v-if="editedItem && !question" @submit.prevent="submitItem">
            <b-form-group
                id="input-group-name"
                :label="$t('device_groups.form.name')"
                label-for="input-name"
            >
                <b-form-input
                    id="input-name"
                    v-model="editedItem.name"
                    :disabled="busy"
                    :state="nameInvalid ? false : null"
                    @input="nameInvalid = false"
                    type="text"
                    :placeholder="$t('device_groups.form.name.placeholder')"
                    required
                />
                <b-form-invalid-feedback :state="!nameInvalid">{{$t('group_name_required')}}</b-form-invalid-feedback>
            </b-form-group>
        </b-form>
        <b-container class="confirmation" v-if="question">
            <h3>{{question.text}}</h3>
            <div class="actionbar">
                <b-button :disabled="busy" @click="question.confirm()" variant="primary">{{$t('device_groups.form.yes')}}</b-button>
                <b-button :disabled="busy" @click="question.decline()" variant="danger">{{$t('device_groups.form.no')}}</b-button>
            </div>
        </b-container>
    </div>
</template>

<script lang="ts">
import Component from "vue-class-component";
import Vue from "vue";
import DeviceGroupRepository, {EditDeviceGroup} from "../../Repository/DeviceGroup/DeviceGroupRepository";
import {IDeviceGroupDto} from "../../Repository/DeviceGroup/Dto/IDeviceDto";
import Loader from "../Common/Loader.vue";

interface ListItem {
    id: number,
    name: string,
    active: boolean,
}

@Component({
    components: { Loader },
    props: {
        groupSelected: { required: false }
    }
})
class DeviceGroupEditor extends Vue {
    items: ListItem[] = [];
    sourceData: IDeviceGroupDto[] = [];
    editedItem: EditDeviceGroup|null = null;
    question: {text: string, confirm: () => void, decline: () => void}|null = null;
    loading: boolean = false;
    busy: boolean = false;
    loadError: boolean = false;
    writeError: boolean = false;
    nameInvalid: boolean = false;
    selectedGroupId: number|null = null;
    disposed: boolean = false;
    listRequestId: number = 0;
    actionRequestId: number = 0;

    beforeDestroy() {
        this.disposed = true;
        this.listRequestId++;
        this.actionRequestId++;
    }

    mounted() {
        this.loadDeviceGroupsData();
    }

    openCreateForm() {
        if (this.disposed || this.loading || this.busy || this.loadError) return;
        this.editedItem = {
            id: null,
            name: ''
        };
        this.question = null;
        this.writeError = false;
        this.nameInvalid = false;
    }

    loadDeviceGroupsData() {
        if (this.disposed || this.loading || this.busy) return;
        const requestId = ++this.listRequestId;
        this.loading = true;
        this.loadError = false;
        return DeviceGroupRepository
            .findAll()
            .then(response => {
                if (this.disposed || requestId !== this.listRequestId) return;
                if (!Array.isArray(response)) throw new Error('Invalid group list');
                this.sourceData = response;
                this.createListData();
            })
            .catch(() => {
                if (!this.disposed && requestId === this.listRequestId) this.loadError = true;
            })
            .finally(() => {
                if (!this.disposed && requestId === this.listRequestId) this.loading = false;
            });
    }

    createListData() {
        const mapGroupToItem = (group: IDeviceGroupDto): ListItem => {
            return {
                id: group.id,
                name: group.name,
                active: group.id === this.selectedGroupId,
            }
        };

        this.items = this.sourceData.map(group => mapGroupToItem(group));
    }

    editItem(item: ListItem) {
        if (this.disposed || this.loading || this.busy || !this.items.some(group => group.id === item.id)) return;
        this.question = null;
        this.writeError = false;
        this.nameInvalid = false;
        this.editedItem = {
            id: item.id,
            name: item.name,
        }
    }

    deleteItem(item: ListItem) {
        if (this.disposed || this.loading || this.busy || !this.items.some(group => group.id === item.id)) return;
        this.writeError = false;
        this.question = {
            text: this.$t('device_groups.form.delete_confirmation').toString(),
            confirm: () => {
                if (this.disposed || this.busy || this.loading || !this.sourceData.some(group => group.id === item.id)) return;
                const requestId = ++this.actionRequestId;
                this.busy = true;
                this.writeError = false;
                return DeviceGroupRepository
                    .delete({
                        id: item.id,
                        name: item.name
                    })
                    .then(() => {
                        this.$store.commit('clearDeviceList');
                        if (this.disposed || requestId !== this.actionRequestId) return;
                        this.sourceData = this.sourceData.filter(group => group.id !== item.id);
                        if (this.selectedGroupId === item.id) {
                            this.selectedGroupId = null;
                            this.$props.groupSelected?.(null);
                        }
                        this.createListData();
                        this.question = null;
                    })
                    .catch(() => {
                        if (!this.disposed && requestId === this.actionRequestId) this.writeError = true;
                    })
                    .finally(() => {
                        if (!this.disposed && requestId === this.actionRequestId) this.busy = false;
                    })
            },
            decline: () => {
                if (this.disposed || this.busy) return;
                this.question = null;
                this.writeError = false;
            }
        }
    }

    cancelEdit() {
        if (this.disposed || this.busy) return;
        this.editedItem = null;
        this.writeError = false;
        this.nameInvalid = false;
    }

    submitItem() {
        if (this.disposed || this.loading || this.busy || !this.editedItem) {
            return;
        }
        const submitted = { id: this.editedItem.id, name: this.editedItem.name.trim() };
        this.nameInvalid = !submitted.name;
        if (this.nameInvalid) return;
        const requestId = ++this.actionRequestId;
        this.busy = true;
        this.writeError = false;
        return DeviceGroupRepository
            .save(submitted)
            .then(response => {
                if (!response || typeof response.id !== 'number' || typeof response.name !== 'string') throw new Error('Invalid group response');
                this.$store.commit('clearDeviceList');
                if (this.disposed || requestId !== this.actionRequestId) return;
                if (submitted.id !== null) {
                    this.sourceData = this.sourceData.map(group => group.id === submitted.id ? response : group);
                } else {
                    this.sourceData.push(response);
                }
                if (submitted.id !== null && this.selectedGroupId === submitted.id) {
                    this.selectedGroupId = response.id;
                    this.$props.groupSelected?.(response);
                }
                this.createListData();
                this.editedItem = null;
            })
            .catch(() => {
                if (!this.disposed && requestId === this.actionRequestId) this.writeError = true;
            })
            .finally(() => {
                if (!this.disposed && requestId === this.actionRequestId) this.busy = false;
            });
    }

    selectGroup(item: ListItem) {
        if (this.disposed || this.loading || this.busy) return;
        this.items.forEach(q => {
            q.active = q === item && !q.active;
        });
        this.selectedGroupId = item.active ? item.id : null;
        if (this.$props.groupSelected) {
            this.$props.groupSelected(item.active ? item : null);
        }
    }
}

export default DeviceGroupEditor;
</script>

<style lang="scss" scoped>
@import '../../custom1.scss';

.device-groups-editor-component {
    .toolbar {
        display: flex;
        justify-content: flex-start;
        flex-direction: row;
        align-items: center;
        padding: 1em 0 1em 0;

        * {
            margin-right: 0.5em;
        }
    }

    .device-groups-list {
        list-style: none;
        padding: 0;
        margin: 0;

        .device-groups-list-item {
            @include dark-list;

            &:hover {
                background-color: lighten($bg-color, 10%);
                cursor: pointer;
            }

            &.active {
                background-color: lighten($bg-color, 25%);
            }

            .list-item-container {
                display: flex;
                flex-direction: row;
                align-items: center;

                .list-item-toolbar {
                    @include no-select;

                    display: flex;
                    flex-direction: row;
                    align-items: center;

                    * {
                        margin-left: 0.5em;

                        &:hover {
                            cursor: pointer;
                        }
                    }
                }
            }
        }
    }

    .confirmation {
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;

        .actionbar {
            display: flex;
            flex-direction: row;
            align-items: center;
            justify-content: center;

            * {
                padding: 0.5em 2em 0.5em 2em;
                min-width: 7em;
                margin-right: 1em;

                *:last-child {
                    margin-right: 0;
                }
            }
        }
    }
}
</style>
